import React, { useEffect, useRef } from 'react';
import { colors } from 'styles/theme/colors';
import { MONO_FONT_FAMILY } from 'styles/fonts';
import { jqPath } from './helpers';
import { DragPathRef } from './dragAndDrop';

function TreeNode({
  name,
  value,
  path,
  depth,
  collapsed,
  onToggle,
  readOnly,
  dragPathRef,
  onLeafActivate,
  pickActive,
  armedPathKey,
  mappedTo,
  visiblePaths,
}: {
  name: string;
  value: any;
  path: string[];
  depth: number;
  collapsed: Set<string>;
  onToggle: (key: string) => void;
  readOnly: boolean;
  dragPathRef: DragPathRef;
  onLeafActivate?: (path: string[]) => void;
  pickActive?: boolean;
  // Path key of the leaf armed for reverse (source-first) click-mapping, if any.
  armedPathKey?: string;
  // Path key → labels of the slots that use it, so a leaf can show "→ source".
  mappedTo?: Record<string, string[]>;
  // Path keys left visible by the field filter (null = no filter active).
  visiblePaths?: Set<string> | null;
}) {
  const isObject =
    value != null && typeof value === 'object' && !Array.isArray(value);
  const isArray = Array.isArray(value);
  const isLeaf = !isObject && !isArray;
  const pathKey = path.join('.');
  const filtering = visiblePaths != null;
  // While filtering, keep branches open so matches are visible.
  const isCollapsed = !filtering && collapsed.has(pathKey);
  const targets = mappedTo?.[pathKey];
  const hasChildren = isObject
    ? Object.keys(value as object).length > 0
    : isArray
      ? (value as any[]).length > 0
      : false;

  const typeColor =
    typeof value === 'number'
      ? colors['blue-7']
      : typeof value === 'string'
        ? colors['ui-cta-primary']
        : typeof value === 'boolean'
          ? colors['amber-3']
          : colors['greyscale-7'];

  const displayValue = isObject
    ? `{${Object.keys(value as object).length}}`
    : isArray
      ? `[${(value as any[]).length}]`
      : JSON.stringify(value);

  const ghostRef = useRef<HTMLDivElement | null>(null);

  const handleDragStart = (e: React.DragEvent) => {
    if (!isLeaf || readOnly) return;
    dragPathRef.current = path;
    e.dataTransfer.setData('text/plain', pathKey);
    e.dataTransfer.effectAllowed = 'copy';

    const ghost = document.createElement('div');
    ghost.textContent = jqPath(path);
    ghost.style.cssText = `position:absolute;top:-100px;left:-100px;padding:4px 10px;background:${colors['ui-cta-primary']};color:${colors['white']};border-radius:6px;font:14px ${MONO_FONT_FAMILY};white-space:nowrap;`;
    document.body.appendChild(ghost);
    ghostRef.current = ghost;
    e.dataTransfer.setDragImage(ghost, 0, 0);
  };

  const handleDragEnd = () => {
    dragPathRef.current = null;
    if (ghostRef.current) {
      ghostRef.current.remove();
      ghostRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      ghostRef.current?.remove();
    };
  }, []);

  // After the hooks: a filtered-out node renders nothing.
  if (filtering && !visiblePaths.has(pathKey)) return null;

  return (
    <div>
      <div
        draggable={isLeaf && !readOnly}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        tabIndex={isLeaf && !readOnly ? (pickActive ? 0 : -1) : undefined}
        role={isLeaf && !readOnly ? 'button' : undefined}
        aria-label={isLeaf && !readOnly ? jqPath(path) : undefined}
        data-leaf-focusable={isLeaf && !readOnly ? 'true' : undefined}
        onKeyDown={
          isLeaf && !readOnly
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onLeafActivate?.(path);
                }
              }
            : undefined
        }
        onClick={isLeaf && !readOnly ? () => onLeafActivate?.(path) : undefined}
        className={`flex select-none items-center gap-1 rounded px-1.5 py-0.5 font-mono text-body-sm focus:outline-none focus:ring-2 focus:ring-teal-t100 ${isLeaf && !readOnly ? `${pickActive ? 'cursor-pointer' : 'cursor-grab'} hover:bg-blue-4` : 'cursor-default'} ${armedPathKey === pathKey ? 'bg-blue-4 ring-2 ring-inset ring-ui-cta-primary' : ''}`}
        style={{ paddingLeft: depth * 16 + 6 }}
      >
        {hasChildren ? (
          // Inert while filtering: branches are forced open, so a toggle
          // would change nothing visible until the filter is cleared.
          <span
            onClick={filtering ? undefined : () => onToggle(pathKey)}
            className={`w-4 text-center text-body-sm text-ui-text-primary-text ${filtering ? 'cursor-default opacity-40' : 'cursor-pointer'}`}
          >
            {isCollapsed ? '▶' : '▼'}
          </span>
        ) : (
          <span
            className="w-4 text-center text-body-sm"
            style={{ color: colors['blue-3'] }}
          >
            ⠿
          </span>
        )}
        <span className="font-normal text-ui-text-primary-text">{name}</span>
        {isLeaf && (
          <span
            className="max-w-[180px] overflow-hidden text-ellipsis whitespace-nowrap"
            style={{ color: typeColor }}
          >
            {displayValue}
          </span>
        )}
        {!isLeaf && (
          <span className="text-body-sm text-ui-text-primary-text">
            {displayValue}
          </span>
        )}
        {isLeaf && targets && targets.length > 0 && (
          <span
            className="ml-auto min-w-0 max-w-[45%] shrink truncate whitespace-nowrap pl-2 font-sans text-body-sm text-ui-cta-primary"
            title={targets.join(', ')}
          >
            → {targets.join(', ')}
          </span>
        )}
      </div>
      {isObject &&
        !isCollapsed &&
        Object.entries(value as Record<string, any>).map(
          ([childKey, childValue]) => (
            <TreeNode
              key={childKey}
              name={childKey}
              value={childValue}
              path={[...path, childKey]}
              depth={depth + 1}
              collapsed={collapsed}
              onToggle={onToggle}
              readOnly={readOnly}
              dragPathRef={dragPathRef}
              onLeafActivate={onLeafActivate}
              pickActive={pickActive}
              armedPathKey={armedPathKey}
              mappedTo={mappedTo}
              visiblePaths={visiblePaths}
            />
          ),
        )}
      {isArray &&
        !isCollapsed &&
        (value as any[]).map((item, index) => (
          <TreeNode
            key={index}
            name={`[${index}]`}
            value={item}
            path={[...path, String(index)]}
            depth={depth + 1}
            collapsed={collapsed}
            onToggle={onToggle}
            readOnly={readOnly}
            dragPathRef={dragPathRef}
            onLeafActivate={onLeafActivate}
            pickActive={pickActive}
            armedPathKey={armedPathKey}
            mappedTo={mappedTo}
            visiblePaths={visiblePaths}
          />
        ))}
    </div>
  );
}

export default TreeNode;
